import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToday } from 'shared/contexts/today';
import { useCreateEntriesFromGoalTemplateMutation } from 'shared/hooks/queries/use-create-entries-from-goal-template-mutation';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { Paths } from 'shared/routes/paths';
import { ID } from 'shared/types/id';
import { replaceUrlParams } from 'shared/utils/replace-url-params';

export const useCreateEntriesFromGoalTemplate = () => {
  const templateIdRef = useRef<ID>();

  const locale = useI18nLocale();
  const { mutateAsync, isSuccess, isPending, error, reset } =
    useCreateEntriesFromGoalTemplateMutation();
  const today = useToday();
  const navigate = useNavigate();

  const submit = useCallback(
    async (id: ID) => {
      templateIdRef.current = id;

      const { mainGoalId } = await mutateAsync({
        id,
        locale,
        startDate: today,
      });

      navigate(replaceUrlParams(Paths.GoalDetail, { id: mainGoalId }));
    },
    [locale, mutateAsync, navigate, today],
  );

  const retry = useCallback(async () => {
    const id = templateIdRef.current;
    if (!id) {
      return;
    }

    const { mainGoalId } = await mutateAsync({
      id,
      locale,
      startDate: today,
    });

    navigate(replaceUrlParams(Paths.GoalDetail, { id: mainGoalId }));
  }, [locale, mutateAsync, navigate, today]);

  const resetAll = useCallback(() => {
    reset();
    templateIdRef.current = undefined;
  }, [reset]);

  return {
    submit,
    retry,
    reset: resetAll,
    error,
    isLoading: isPending,
    isSuccess,
  };
};
